import React, { Dispatch, FunctionComponent, useState } from "react";

/* Validation */
import { Formik, useFormikContext } from "formik";
import { validationSchema } from "./formConfig";

/* API */
import axios from "axios";
import { Endpoints } from "@internal/common";
import { Button, Flex, HelpText, Title } from "@gjensidige/builders-components";
import FormAlert from "./components/FormAlert";
import FormInput from "./components/FormInput";
import FormInputGroup from "./components/FormInputGroup";
import FormDatepicker from "./components/FormDatepicker";
import FormTextArea from "./components/FormTextArea";
import { ClaimReportLiabilityDamageFormData } from "./ClaimModels";

export interface IProps {
  claimType: string;
}

interface FormData extends ClaimReportLiabilityDamageFormData {
  claimType: string;
}

const ClaimReportLiabilityDamage: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState<FormData>({
    claimType: props.claimType,
    policyNumber: "",
    companyName: "",
    cvrNumber: "",
    contactPersonName: "",
    contactPersonPhoneNumber: "",
    email: "",
    referenceNumber: "",
    isAnyoneInjured: "",
    damageLocation: "",
    damageDescription: "",
    damageReportedOwnInsuranceCompany: "",
    claimFromInjuredParty: "",
    claimAmount: "",
    claimDate: "",
    injuredPartyName: "",
    injuredPartyPhoneNumber: "",
    injuredPartyEmail: "",
    injuredPartyAddress: "",
    injuredPartyPostalCode: "",
    injuredPartyCVRNumber: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    setIsLoading(true);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => {
        setIsLoading(false);
        setApiError(true);
      });
  };

  return (
    <>
      {submitted ? (
        <FormAlert
          severity="success"
          title="Tak for din anmeldelse"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse."
        />
      ) : (
        <>
          <Title size={5}>Forsikringstager</Title>
          <HelpText>
            Udfyld gerne oplysningerne så præcist som muligt, så vi kan komme i
            kontakt med dig/din virksomhed. Hvis du udfylder alle felterne med
            relevant information, kan vi hurtigere behandle din anmeldelse.
          </HelpText>
          <Formik
            enableReinitialize
            initialValues={{
              policyNumber: formData.policyNumber,
              companyName: formData.companyName,
              cvrNumber: formData.cvrNumber,
              contactPersonName: formData.contactPersonName,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              email: formData.email,
              referenceNumber: formData.referenceNumber,
              isAnyoneInjured: formData.isAnyoneInjured,
              damageLocation: formData.damageLocation,
              damageDescription: formData.damageDescription,
              damageReportedOwnInsuranceCompany:
                formData.damageReportedOwnInsuranceCompany,
              claimFromInjuredParty: formData.claimFromInjuredParty,
              claimAmount: formData.claimAmount,
              claimDate: formData.claimDate,
              injuredPartyName: formData.injuredPartyName,
              injuredPartyPhoneNumber: formData.injuredPartyPhoneNumber,
              injuredPartyEmail: formData.injuredPartyEmail,
              injuredPartyAddress: formData.injuredPartyAddress,
              injuredPartyPostalCode: formData.injuredPartyPostalCode,
              injuredPartyCVRNumber: formData.injuredPartyCVRNumber,
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            <RenderForm isLoading={isLoading} setFormData={setFormData} />
          </Formik>
        </>
      )}
      {apiError && (
        <FormAlert
          severity="error"
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
        />
      )}
    </>
  );
};

interface RenderProps {
  isLoading: boolean;
  setFormData: Dispatch<React.SetStateAction<FormData>>;
}

const RenderForm: FunctionComponent<RenderProps> = ({
  isLoading,
  setFormData,
}) => {
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values: formData,
  } = useFormikContext<FormData>();

  const todaysDate = new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <form onSubmit={handleSubmit}>
      <Flex layout={1}>
        <FormInput
          id="policyNumber"
          labelText="Policenummer"
          name="policyNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("policyNumber", event.target.value);
            setFormData({
              ...formData,
              policyNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.policyNumber && errors.policyNumber)}
          errorText={errors.policyNumber}
          value={formData.policyNumber}
        />
        <FormInput
          id="company-name"
          labelText="Virksomhedens navn"
          name="companyName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("companyName", event.target.value);
            setFormData({
              ...formData,
              companyName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.companyName && errors.companyName)}
          errorText={errors.companyName}
          value={formData.companyName}
        />
        <FormInput
          id="cvr-number"
          labelText="CVR-nr (frivilligt)"
          name="cvrNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("cvrNumber", event.target.value);
            setFormData({
              ...formData,
              cvrNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.cvrNumber && errors.cvrNumber)}
          errorText={errors.cvrNumber}
          value={formData.cvrNumber}
        />

        <Title size={5}>Kontaktperson hos forsikringstager</Title>

        <FormInput
          id="contactPersonName"
          labelText="Navn (kontaktperson)"
          name="contactPersonName"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonName", event.target.value);
            setFormData({
              ...formData,
              contactPersonName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.contactPersonName && errors.contactPersonName)}
          errorText={errors.contactPersonName}
          value={formData.contactPersonName}
        />
        <FormInput
          id="contactPersonPhoneNumber"
          labelText="Telefonnummer (kontaktperson)"
          name="contactPersonPhoneNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("contactPersonPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              contactPersonPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.contactPersonPhoneNumber &&
              errors.contactPersonPhoneNumber
            )
          }
          errorText={errors.contactPersonPhoneNumber}
          value={formData.contactPersonPhoneNumber}
        />
        <FormInput
          id="email"
          labelText="Email (kontaktperson)"
          name="email"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("email", event.target.value);
            setFormData({
              ...formData,
              email: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.email && errors.email)}
          errorText={errors.email}
          value={formData.email}
        />
        <FormInput
          id="reference-number"
          labelText="Evt. referencenummer (frivilligt)"
          name="referenceNumber"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("referenceNumber", event.target.value);
            setFormData({
              ...formData,
              referenceNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.referenceNumber && errors.referenceNumber)}
          errorText={errors.referenceNumber}
          value={formData.referenceNumber}
        />

        <Title size={5}>Oplysninger om skaden</Title>

        <FormInputGroup
          labelText="Er nogen personer kommet til skade?"
          helpText="Vi har brug for at vide, om der er sket personskade. Klik ja, hvis en eller flere personer er kommet til skade (fysisk eller psykisk), da uheldet skete."
          id="isAnyoneInjured"
          onChange={(event) => {
            setFormData({
              ...formData,
              isAnyoneInjured: event.at(0)!,
            });
            setFieldValue("isAnyoneInjured", event.at(0));
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.isAnyoneInjured}
          onBlur={handleBlur}
          invalid={!!(touched.isAnyoneInjured && errors.isAnyoneInjured)}
          errorText={errors.isAnyoneInjured}
        />
        <FormTextArea
          id="damageLocation"
          labelText="Hvor er skaden sket?"
          helpText="Skriv adressen på det sted, hvor skaden skete. Hvis stedet ikke har en adresse, så beskriv stedets placering så præcist som muligt."
          maxLength={500}
          value={formData.damageLocation}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageLocation", event.target.value);
            setFormData({
              ...formData,
              damageLocation: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageLocation && errors.damageLocation)}
          errorText={errors.damageLocation}
        />
        <FormTextArea
          id="damageDescription"
          labelText="Hvad er der sket og hvordan skete det?"
          helpText="Beskriv forløbet omkring skaden så præcist som muligt. Hvem var tilstede? Hvad skete der? Hvem gjorde hvad? Hvad udløste den hændelse, som førte til skaden? Mener du, at der tale om en fejl fra din/din virksomheds side?"
          maxLength={500}
          value={formData.damageDescription}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFieldValue("damageDescription", event.target.value);
            setFormData({
              ...formData,
              damageDescription: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.damageDescription && errors.damageDescription)}
          errorText={errors.damageDescription}
        />
        <FormInputGroup
          labelText="Er skaden anmeldt til skadelidtes egen forsikring?"
          helpText="Sæt kryds i ja, hvis du ved, at skadelidte har taget kontakt til sit eget forsikringsselskab omkring skaden (fx en bilforsikring, indboforsikring, husforsikring, entrepriseforsikring el.lign.). Det er vigtig information for os, så vi kan behandle sagen hurtigere og mere korrekt."
          id="damageReportedOwnInsuranceCompany"
          onChange={(event) => {
            setFormData({
              ...formData,
              damageReportedOwnInsuranceCompany: event.at(0) ?? "",
            });
            setFieldValue(
              "damageReportedOwnInsuranceCompany",
              event.at(0) ?? "",
            );
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
            {
              label: "Ved ikke",
              value: "Ved ikke",
            },
          ]}
          type="radio"
          value={formData.damageReportedOwnInsuranceCompany}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.damageReportedOwnInsuranceCompany &&
              errors.damageReportedOwnInsuranceCompany
            )
          }
          errorText={errors.damageReportedOwnInsuranceCompany}
        />
        <FormInputGroup
          labelText="Har du modtaget et krav fra skadelidte?"
          helpText="Her skal du klikke ”ja”, hvis du allerede har modtaget et krav fra den virksomhed eller person, som er ramt af skaden."
          id="claimFromInjuredParty"
          onChange={(event) => {
            setFormData({
              ...formData,
              claimFromInjuredParty: event.at(0) ?? "",
            });
            setFieldValue("claimFromInjuredParty", event.at(0) ?? "");
          }}
          options={[
            {
              label: "Ja",
              value: "Ja",
            },
            {
              label: "Nej",
              value: "Nej",
            },
          ]}
          type="radio"
          value={formData.claimFromInjuredParty}
          onBlur={handleBlur}
          invalid={
            !!(touched.claimFromInjuredParty && errors.claimFromInjuredParty)
          }
          errorText={errors.claimFromInjuredParty}
        />
        <FormInput
          id="claimAmount"
          labelText="Hvor stort er kravet? (frivilligt)"
          helpText="Hvis du har modtaget et krav, så oplys venligst kravets størrelse (beløb i kr.)."
          name="claimAmount"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("claimAmount", event.target.value);
            setFormData({
              ...formData,
              claimAmount: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.claimAmount && errors.claimAmount)}
          errorText={errors.claimAmount}
          value={formData.claimAmount}
        />
        <FormDatepicker
          labelText="Hvilken dato har du modtaget kravet? (frivilligt)"
          helpText="Hvis du har modtaget et krav fra den skadelidte, så oplys venligst hvilken dato du modtog kravet."
          id="claimDate"
          onChange={(value) => {
            if (value) {
              setFormData({ ...formData, claimDate: value?.toDateString() });
              setFieldValue("claimDate", value?.toDateString());
            }
          }}
          invalid={!!(touched.claimDate && errors.claimDate)}
          min={todaysDate}
          errorText={errors.claimDate}
          value={formData.claimDate}
        />
        <Title size={5}>Oplysninger om skadelidte</Title>
        <FormInput
          id="injuredPartyName"
          labelText="Navn"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyName", event.target.value);
            setFormData({
              ...formData,
              injuredPartyName: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.injuredPartyName && errors.injuredPartyName)}
          errorText={errors.injuredPartyName}
          value={formData.injuredPartyName}
        />
        <FormInput
          id="injuredPartyPhoneNumber"
          labelText="Tlf.nr"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyPhoneNumber", event.target.value);
            setFormData({
              ...formData,
              injuredPartyPhoneNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(
              touched.injuredPartyPhoneNumber && errors.injuredPartyPhoneNumber
            )
          }
          errorText={errors.injuredPartyPhoneNumber}
          value={formData.injuredPartyPhoneNumber}
        />
        <FormInput
          id="injuredPartyEmail"
          labelText="E-mail (frivilligt)"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyEmail", event.target.value);
            setFormData({
              ...formData,
              injuredPartyEmail: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={!!(touched.injuredPartyEmail && errors.injuredPartyEmail)}
          errorText={errors.injuredPartyEmail}
          value={formData.injuredPartyEmail}
        />
        <FormInput
          id="injuredPartyAddress"
          labelText="Adresse (frivilligt)"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyAddress", event.target.value);
            setFormData({
              ...formData,
              injuredPartyAddress: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.injuredPartyAddress && errors.injuredPartyAddress)
          }
          errorText={errors.injuredPartyAddress}
          value={formData.injuredPartyAddress}
        />
        <FormInput
          id="injuredPartyPostalCode"
          labelText="Postnummer (frivilligt)"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyPostalCode", event.target.value);
            setFormData({
              ...formData,
              injuredPartyPostalCode: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.injuredPartyPostalCode && errors.injuredPartyPostalCode)
          }
          errorText={errors.injuredPartyPostalCode}
          value={formData.injuredPartyPostalCode}
        />
        <FormInput
          id="injuredPartyCVRNumber"
          labelText="Evt. CVR-nr. (frivilligt)"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue("injuredPartyCVRNumber", event.target.value);
            setFormData({
              ...formData,
              injuredPartyCVRNumber: event.target.value,
            });
          }}
          onBlur={handleBlur}
          invalid={
            !!(touched.injuredPartyCVRNumber && errors.injuredPartyCVRNumber)
          }
          errorText={errors.injuredPartyCVRNumber}
          value={formData.injuredPartyCVRNumber}
        />
        <Button loading={isLoading} type="submit" flexible>
          Send
        </Button>
      </Flex>
    </form>
  );
};

export default ClaimReportLiabilityDamage;
